import React from "react";
import { Link } from "react-router-dom";
import tools from "../../../../helpers/tools";
import { estAnonymise } from "../../../../helpers/allocataire";
import { Pill } from "../../../common/DataList";

// Briques partagées par les deux listes et les deux fiches de l'écran
// Sanctions France Travail. Elles vivaient dans la fiche des propositions ; la
// fiche des déclarations en a le même besoin, et deux copies auraient divergé
// au premier ajustement.

// Couleurs des points de chronologie : bleu pour ce qui vient de France
// Travail, vert pour ce qui aboutit, rouge pour un échec.
export const COULEUR_FT = "rgb(14, 107, 176)";
export const COULEUR_OK = "rgb(10, 110, 14)";
export const COULEUR_KO = "rgb(191, 47, 56)";
export const COULEUR_NEUTRE = "rgb(70, 88, 106)";

export const formatDateHeure = (d) => tools.formatDate(d, "dd/MM/yyyy à HH:mm");

export const nomAllocataire = (allocataire) =>
  allocataire ? `${allocataire.nom || ""} ${allocataire.prenom || ""}`.trim() : "";

/** Un fait « libellé / valeur » qui disparaît quand il n'y a rien à dire. */
export const Fait = ({ label, children }) =>
  children === undefined || children === null || children === "" ? null : (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );

/** Une ligne d'échange avec France Travail, avec son code de retour. */
export const LigneReponse = ({ ton, icon, children, code, className = "" }) => (
  <li className={`reponse-ft__row reponse-ft--${ton} ${className}`.trim()}>
    <i className={`fa fa-${icon}`} aria-hidden="true" />
    <span className="reponse-ft__text">
      {children}
      {code && <span className="reponse-ft__code">code FT {code}</span>}
    </span>
  </li>
);

// --- Cellules de tableau (kit DataList) ------------------------------------

/**
 * Nom de l'allocataire, lien vers sa fiche dans un nouvel onglet — la liste
 * reste ouverte — ou « Non rattaché » quand France Travail ne nous a pas
 * permis de le retrouver. `secondaire` : identifiants sous le nom.
 */
export const CelluleAllocataire = ({ allocataire, secondaire = null }) => {
  const nom = nomAllocataire(allocataire);
  return (
    <>
      {allocataire && nom ? (
        <Link
          to={tools.getRecipientLinkFromParams(allocataire.type, allocataire.id)}
          className="recipient-name"
          target="_blank"
          rel="noopener noreferrer"
        >
          {nom}
        </Link>
      ) : (
        <span className="text-muted">Non rattaché</span>
      )}
      {estAnonymise(allocataire) && (
        <Pill tone="muted" icon="fa-user-secret" className="ml-2">
          Anonymisé
        </Pill>
      )}
      {secondaire && <span className="cell-secondary cell-mono">{secondaire}</span>}
    </>
  );
};

/** Ligne secondaire d'une cellule, teintée selon l'état qu'elle rapporte. */
export const Note = ({ ton = null, icon = null, children, className = "" }) => (
  <span className={`cell-secondary${ton ? ` cell-note--${ton}` : ""} ${className}`.trim()}>
    {icon && <i className={`fa fa-${icon} mr-1`} aria-hidden="true" />}
    {children}
  </span>
);

/** Bouton « voir la fiche » d'une ligne : icône seule, libellé pour les lecteurs d'écran. */
export const BoutonVoir = ({ onClick, label, className = "" }) => (
  <button
    type="button"
    className={`btn btn-outline-primary btn-icon ${className}`.trim()}
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    <i className="fa fa-eye" aria-hidden="true" />
  </button>
);

/**
 * Bouton « relancer » d'une ligne : la même action que dans la fiche, sans
 * l'ouvrir. Orange quand un blocage est à réparer, neutre quand il s'agit
 * juste de ne pas attendre le passage automatique. L'API dit si la relance a
 * un sens (`relancable`) : le bouton ne s'affiche pas sinon.
 */
export const BoutonRelancer = ({ onClick, label, enCours = false, alerte = false, className = "" }) => (
  <button
    type="button"
    className={`btn ${alerte ? "btn-warning" : "btn-outline-secondary"} btn-icon ml-1 ${className}`.trim()}
    onClick={onClick}
    disabled={enCours}
    aria-busy={enCours ? true : undefined}
    aria-label={label}
    title={label}
  >
    <i className={`fa ${enCours ? "fa-spinner fa-spin" : "fa-redo"}`} aria-hidden="true" />
  </button>
);

/**
 * Demande confirmation avant une relance en masse, dans la modale du
 * back-office (celle du store, comme partout ailleurs) : la relance porte sur
 * tous les dossiers en échec, pas sur la page affichée, et peut déclencher
 * plusieurs centaines d'appels à France Travail. Le bouton « Fermer » de la
 * modale tient lieu d'annulation ; `phrase` dit ce qui repart, au pluriel
 * du dossier concerné.
 */
export const ConfirmationRelanceEnMasse = ({ phrase }) => (
  <div className="confirmation-relance-en-masse">
    <h5 className="mb-3">
      <i className="fa fa-exclamation-triangle text-warning mr-2" aria-hidden="true" />
      Relancer tout ce qui est en échec ?
    </h5>
    <p>{phrase}, pas seulement sur cette page.</p>
    <p className="mb-0">
      Cela peut déclencher plusieurs centaines d'appels à France Travail, cadencés par l'application.
    </p>
  </div>
);

export const ouvrirConfirmationRelanceEnMasse = (mActions, { phrase, onConfirmer }) => {
  const fermer = () => mActions.updateModal({ isOpen: false, content: null });
  mActions.updateModal({
    isOpen: true,
    content: <ConfirmationRelanceEnMasse phrase={phrase} />,
    actions: (
      <button
        type="button"
        className="btn btn-warning confirmation-relance-en-masse__confirmer"
        onClick={() => {
          fermer();
          onConfirmer();
        }}
      >
        <i className="fa fa-redo mr-1" aria-hidden="true" />
        Relancer tout ce qui est en échec
      </button>
    ),
  });
};

/** Bilan d'une relance en masse, prêt à afficher en notification. */
export const libelleBilanRelance = ({ relancees = 0, ignorees = 0, echouees = 0 }, objet) => {
  const parts = [`${relancees} ${objet}${relancees > 1 ? "s" : ""} remise${relancees > 1 ? "s" : ""} en file`];
  if (ignorees) parts.push(`${ignorees} sans objet`);
  if (echouees) parts.push(`${echouees} en échec`);
  return parts.join(", ") + ".";
};
